import Close from '@mui/icons-material/Close'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import { GreenText, NumericTextField as BaseNumericTextField, OrderableTableCell } from 'components'
import { saveInvoicingServices } from 'controllers/invoicing/index'
import { InvoicingDto, VehicleInvoicingServiceDto } from 'dtos/invoicing'
import { enqueueSnackbar } from 'notistack'
import { ComponentProps, useEffect, useState } from 'react'
import { clientSideOrderBy, clientSidePagination, currencyInputPattern } from 'utils'

interface VehiclesEligibleForInvoicingCardProps {
  invoicingServices: VehicleInvoicingServiceDto[]
  refreshInvoicing: (invoicing: InvoicingDto) => void
}

type InvoicingAmountKey =
  | 'invoicingImportCharge'
  | 'invoicingDuty'
  | 'invoicingInboundTransportation'
  | 'invoicingClusterConversion'
  | 'invoicingClusterProgramming'
  | 'invoicingClusterReplacement'
  | 'invoicingTitleFees'
  | 'invoicingRecallExpenses'
  | 'invoicingFedEx'
  | 'invoicingFedExAPI'
  | 'invoicingReplacementLabel'
  | 'invoicingRepairs'
  | 'invoicingVehicleCleanUp'
  | 'invoicingMiscellaneous'
  | 'invoicingOutboundTransportation'

const parseCurrencyInput = (input: string): number => {
  const amount = parseFloat(input)
  return Number.isFinite(amount) ? amount : 0
}

type InvoicingCurrencyTextFieldProps = ComponentProps<typeof BaseNumericTextField>

function NumericTextField({ onChange, ...props }: InvoicingCurrencyTextFieldProps) {
  return (
    <BaseNumericTextField
      {...props}
      allowDecimal
      onChange={event => {
        if (currencyInputPattern.test(event.target.value)) {
          onChange?.(event)
        }
      }}
    />
  )
}

const invoicingServicesSelectionCellSx = {
  boxSizing: 'border-box',
  maxWidth: 40,
  minWidth: 40,
  padding: '2px 4px !important',
  textAlign: 'left',
  verticalAlign: 'middle',
  width: 40
}

export default function VehiclesEligibleForInvoicingCard({ invoicingServices, refreshInvoicing }: VehiclesEligibleForInvoicingCardProps) {
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isSavingAndMarkingReadyToInvoice, setIsSavingAndMarkingReadyToInvoice] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof VehicleInvoicingServiceDto>('importerStockNumber')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [selected, setSelected] = useState<VehicleInvoicingServiceDto[]>([])

  const [importCharge, setImportCharge] = useState<string>('')
  const [duty, setDuty] = useState<string>('')
  const [inboundTransportation, setInboundTransportation] = useState<string>('')
  const [clusterConversion, setClusterConversion] = useState<string>('')
  const [clusterProgramming, setClusterProgramming] = useState<string>('')
  const [clusterReplacement, setClusterReplacement] = useState<string>('')
  const [titleFee, setTitleFee] = useState<string>('')
  const [recallExpenses, setRecallExpenses] = useState<string>('')
  const [fedEx, setFedEx] = useState<string>('')
  const [fedExAPI, setFedExAPI] = useState<string>('')
  const [replacementLabel, setReplacementLabel] = useState<string>('')
  const [repairs, setRepairs] = useState<string>('')
  const [vehicleCleanUp, setVehicleCleanUp] = useState<string>('')
  const [miscellaneous, setMiscellaneous] = useState<string>('')
  const [outboundTransportation, setOutboundTransportation] = useState<string>('')

  const orderedVehicles = [...invoicingServices].sort(clientSideOrderBy<VehicleInvoicingServiceDto>(orderBy, order))
  const visibleVehicles = orderedVehicles.slice(...clientSidePagination(page, pageSize))
  const selectedVisibleCount = visibleVehicles.filter(vehicle => selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)).length
  const allVisibleSelected = visibleVehicles.length > 0 && selectedVisibleCount === visibleVehicles.length
  const selectAllTooltip = `${allVisibleSelected ? 'Deselect' : 'Select'} all vehicles on this page (${visibleVehicles.length})`

  useEffect(() => {
    setPage(0)
    setSelected([])
  }, [pageSize])

  useEffect(() => {
    setIsSubmitting(isSaving || isSavingAndMarkingReadyToInvoice)
  }, [isSaving, isSavingAndMarkingReadyToInvoice])

  useEffect(() => {
    if (selected.length === 0) {
      setImportCharge('')
      setDuty('')
      setInboundTransportation('')
      setClusterConversion('')
      setClusterProgramming('')
      setClusterReplacement('')
      setTitleFee('')
      setRecallExpenses('')
      setFedEx('')
      setFedExAPI('')
      setReplacementLabel('')
      setRepairs('')
      setVehicleCleanUp('')
      setMiscellaneous('')
      setOutboundTransportation('')
    }
  }, [selected.length])

  useEffect(() => {
    setSelected(currentSelected =>
      currentSelected
        .filter(selectedVehicle => invoicingServices.some(vehicle => vehicle.vehicleGuid === selectedVehicle.vehicleGuid))
        .map(selectedVehicle => {
          const currentVehicle = invoicingServices.find(vehicle => vehicle.vehicleGuid === selectedVehicle.vehicleGuid)
          return currentVehicle ? { ...currentVehicle, ...selectedVehicle } : selectedVehicle
        })
    )
  }, [invoicingServices])

  // BR-5 / AC-5: bulk outbound updates may affect only eligible selected VINs.
  const selectedOutboundTransportationVehicles = selected.filter(vehicle => vehicle.isOutboundTransportationEnabled)

  const onChangeSortByDirection = (propertyName: keyof VehicleInvoicingServiceDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  const toggleSelectAll = () => {
    setSelected(currentSelected => {
      if (allVisibleSelected) {
        return currentSelected.filter(selectedVehicle => !visibleVehicles.some(vehicle => vehicle.vehicleGuid === selectedVehicle.vehicleGuid))
      }

      const selectedVehicleGuids = new Set(currentSelected.map(vehicle => vehicle.vehicleGuid))
      return [...currentSelected, ...visibleVehicles.filter(vehicle => !selectedVehicleGuids.has(vehicle.vehicleGuid))]
    })
  }

  const each = (key: keyof VehicleInvoicingServiceDto, input: string): void => {
    const amount = parseCurrencyInput(input).toFixed(2)
    const vehiclesToUpdate = key === 'invoicingOutboundTransportation' ? selectedOutboundTransportationVehicles : selected

    setSelected(currentSelected =>
      currentSelected.map(v => (vehiclesToUpdate.some(vehicle => vehicle.vehicleGuid === v.vehicleGuid) ? { ...v, [key]: amount } : v))
    )
  }

  const split = (key: keyof VehicleInvoicingServiceDto, input: string): void => {
    const vehiclesToUpdate = key === 'invoicingOutboundTransportation' ? selectedOutboundTransportationVehicles : selected

    if (vehiclesToUpdate.length === 0) {
      return
    }

    const amount = (parseCurrencyInput(input) / vehiclesToUpdate.length).toFixed(2)
    setSelected(currentSelected =>
      currentSelected.map(v => (vehiclesToUpdate.some(vehicle => vehicle.vehicleGuid === v.vehicleGuid) ? { ...v, [key]: amount } : v))
    )
  }

  const selectAllCheckbox = (
    <Checkbox
      checked={allVisibleSelected}
      disabled={isSubmitting || visibleVehicles.length === 0}
      id='invoicing-services-select-all'
      indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
      inputProps={{ 'aria-label': selectAllTooltip }}
      onChange={toggleSelectAll}
      size='small'
    />
  )

  const renderVehicleAmount = (vehicle: VehicleInvoicingServiceDto, key: InvoicingAmountKey) => {
    const selectedVehicle = selected.find(selectedVehicle => selectedVehicle.vehicleGuid === vehicle.vehicleGuid)
    const outboundTransportationUnavailable = key === 'invoicingOutboundTransportation' && !vehicle.isOutboundTransportationEnabled
    const value = outboundTransportationUnavailable ? 0 : (selectedVehicle?.[key] ?? vehicle[key] ?? 0)

    if (!selectedVehicle) {
      return <Typography component='span'>{`$${Number(value).toFixed(2)}`}</Typography>
    }

    const amountField = (
      <NumericTextField
        allowDecimal
        disabled={isSubmitting || outboundTransportationUnavailable}
        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
        id={`invoicing-services-${key}-${vehicle.vehicleGuid}`}
        onChange={e => {
          if (outboundTransportationUnavailable) {
            return
          }

          setSelected(currentSelected =>
            currentSelected.map(selectedVehicle =>
              selectedVehicle.vehicleGuid === vehicle.vehicleGuid
                ? ({ ...selectedVehicle, [key]: e.target.value } as VehicleInvoicingServiceDto)
                : selectedVehicle
            )
          )
        }}
        size='small'
        sx={{ width: '100%' }}
        value={value}
      />
    )

    if (outboundTransportationUnavailable && vehicle.outboundTransportationDisabledReason) {
      return (
        <Tooltip arrow placement='top' title={vehicle.outboundTransportationDisabledReason}>
          <span style={{ display: 'block', width: '100%' }}>{amountField}</span>
        </Tooltip>
      )
    }

    return amountField
  }

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={12} container alignItems='center' spacing={1}>
            <Grid item>
              <Typography fontWeight='bold' variant='h6'>
                Invoicing Services
              </Typography>
            </Grid>
            {selected.length > 0 && (
              <Grid item>
                <Chip
                  deleteIcon={
                    <Tooltip title='Clear all selections'>
                      <span>
                        <IconButton aria-label='Clear all selections' disabled={isSubmitting} id='invoicing-services-clear-selection' size='small'>
                          <Close fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                  label={`${selected.length} selected`}
                  onDelete={() => setSelected([])}
                />
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table
                size='small'
                sx={theme => ({
                  tableLayout: 'fixed',
                  minWidth: '2800px',
                  '& .MuiTableCell-head': {
                    color: theme.palette.primary.main,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    lineHeight: 1.25,
                    py: 0.75,
                    px: 0.75,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    verticalAlign: 'bottom'
                  },
                  '& .MuiTableCell-body': {
                    fontSize: '9pt',
                    lineHeight: 1.35,
                    py: 0.5,
                    px: 0.75,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    verticalAlign: 'top'
                  },
                  '& .MuiTableCell-body .MuiTypography-root': {
                    fontSize: '9pt',
                    lineHeight: 1.2
                  },
                  '& .invoicing-services-vehicle-row > .MuiTableCell-root': {
                    verticalAlign: 'middle'
                  },
                  '& .MuiTableBody-root > .MuiTableRow-root:first-of-type > .MuiTableCell-root': {
                    borderBottom: 0
                  },
                  '& .MuiTableCell-paddingCheckbox': {
                    py: 0.25,
                    px: 0.5,
                    width: 40,
                    verticalAlign: 'middle'
                  },
                  '& .MuiTableCell-body .MuiInputBase-input': {
                    fontSize: '9pt',
                    py: 0.375,
                    px: 0.75
                  },
                  '& .MuiTableCell-body .MuiOutlinedInput-root': {
                    minHeight: 28
                  },
                  '& .MuiTableCell-body .MuiButton-root': {
                    fontSize: '0.8rem',
                    lineHeight: 1.25,
                    minWidth: 0,
                    py: 0.375,
                    px: 0.75,
                    minHeight: 28
                  },
                  '& col:nth-child(n+3):nth-child(-n+17)': {
                    width: '160px !important'
                  }
                })}
              >
                <colgroup>
                  <col style={{ width: '40px' }} />
                  <col style={{ width: '160px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '160px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '160px' }} />
                  <col style={{ width: '200px' }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox' sx={invoicingServicesSelectionCellSx}></TableCell>

                  <TableCell sx={{ width: '160px', minWidth: '160px' }}></TableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingImportCharge'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Import Charge
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingDuty'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Duty
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingInboundTransportation'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='160px'
                  >
                    Inbound Transportation
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingClusterConversion'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Cluster Conversion
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingClusterProgramming'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Cluster Programming
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingClusterReplacement'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Cluster Replacement
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingTitleFees'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Title Fee
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingRecallExpenses'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Recall Expenses
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingFedEx'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    FedEx
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingFedExAPI'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    FedEx API
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingReplacementLabel'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Replacement Label
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingRepairs'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Repairs
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingVehicleCleanUp'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Vehicle Clean Up
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingMiscellaneous'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='140px'
                  >
                    Miscellaneous
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingOutboundTransportation'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='160px'
                  >
                    Outbound Transportation
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleInvoicingServiceDto>
                    disabled
                    keyName='invoicingNotes'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                    width='200px'
                  >
                    Invoice Notes
                  </OrderableTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell
                      padding='checkbox'
                      rowSpan={2}
                      sx={{
                        ...invoicingServicesSelectionCellSx,
                        borderBottom: '1px solid rgba(224, 224, 224, 1) !important'
                      }}
                    >
                      {isSubmitting || visibleVehicles.length === 0 ? selectAllCheckbox : <Tooltip title={selectAllTooltip}>{selectAllCheckbox}</Tooltip>}
                    </TableCell>

                    <TableCell sx={{ width: '160px', minWidth: '160px' }}></TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setImportCharge(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={importCharge ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setDuty(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={duty ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setInboundTransportation(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={inboundTransportation ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setClusterConversion(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={clusterConversion ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setClusterProgramming(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={clusterProgramming ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setClusterReplacement(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={clusterReplacement ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setTitleFee(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={titleFee ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setRecallExpenses(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={recallExpenses ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setFedEx(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={fedEx ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setFedExAPI(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={fedExAPI ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setReplacementLabel(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={replacementLabel ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setRepairs(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={repairs ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setVehicleCleanUp(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={vehicleCleanUp ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selected.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        onChange={e => setMiscellaneous(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={miscellaneous ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                      <NumericTextField
                        allowDecimal
                        disabled={isSubmitting || selectedOutboundTransportationVehicles.length === 0}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        id='invoicing-services-outbound-transportation-bulk'
                        onChange={e => setOutboundTransportation(e.target.value)}
                        size='small'
                        sx={{ width: '100%' }}
                        value={outboundTransportation ?? ''}
                      />
                    </TableCell>

                    <TableCell sx={{ width: '160px', minWidth: '160px' }}></TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ width: '160px', minWidth: '160px' }}></TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingImportCharge', importCharge)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingDuty', duty)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingInboundTransportation', inboundTransportation)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => split('invoicingInboundTransportation', inboundTransportation)}
                            size='small'
                            variant='contained'
                          >
                            SPLIT
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingClusterConversion', clusterConversion)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingClusterProgramming', clusterProgramming)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingClusterReplacement', clusterReplacement)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingTitleFees', titleFee)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingRecallExpenses', recallExpenses)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingFedEx', fedEx)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingFedExAPI', fedExAPI)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingReplacementLabel', replacementLabel)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingRepairs', repairs)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingVehicleCleanUp', vehicleCleanUp)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selected.length === 0}
                            fullWidth
                            onClick={() => each('invoicingMiscellaneous', miscellaneous)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selectedOutboundTransportationVehicles.length === 0}
                            fullWidth
                            id='invoicing-services-outbound-transportation-each'
                            onClick={() => each('invoicingOutboundTransportation', outboundTransportation)}
                            size='small'
                            variant='contained'
                          >
                            EACH
                          </Button>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Button
                            disabled={isSubmitting || selectedOutboundTransportationVehicles.length === 0}
                            fullWidth
                            id='invoicing-services-outbound-transportation-split'
                            onClick={() => split('invoicingOutboundTransportation', outboundTransportation)}
                            size='small'
                            variant='contained'
                          >
                            SPLIT
                          </Button>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell sx={{ width: '200px', minWidth: '200px' }}></TableCell>
                  </TableRow>

                  {visibleVehicles.map((vehicle, index) => (
                      <TableRow
                        className='invoicing-services-vehicle-row'
                        key={vehicle.vehicleGuid}
                        sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}
                      >
                        <TableCell padding='checkbox' sx={invoicingServicesSelectionCellSx}>
                          <Checkbox
                            checked={selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)}
                            disabled={isSubmitting}
                            id={`invoicing-services-select-${vehicle.vehicleGuid}`}
                            inputProps={{ 'aria-label': `Select vehicle ${vehicle.vin}` }}
                            onChange={() => {
                              setSelected(currentSelected =>
                                currentSelected.some(value => value.vehicleGuid === vehicle.vehicleGuid)
                                  ? currentSelected.filter(value => value.vehicleGuid !== vehicle.vehicleGuid)
                                  : [...currentSelected, vehicle]
                              )
                            }}
                            size='small'
                          />
                        </TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                          <Typography>{vehicle.importerStockNumber}</Typography>
                          <Typography>{vehicle.buyerExporterCompany}</Typography>
                          <Typography>{vehicle.papsNumber}</Typography>
                          <Typography>{vehicle.vin}</Typography>
                          <Typography>
                            {vehicle.year} {vehicle.make} {vehicle.model}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                          {renderVehicleAmount(vehicle, 'invoicingImportCharge')}
                        </TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>{renderVehicleAmount(vehicle, 'invoicingDuty')}</TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                          {renderVehicleAmount(vehicle, 'invoicingInboundTransportation')}
                        </TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                          {renderVehicleAmount(vehicle, 'invoicingClusterConversion')}
                        </TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                          {renderVehicleAmount(vehicle, 'invoicingClusterProgramming')}
                        </TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                          {renderVehicleAmount(vehicle, 'invoicingClusterReplacement')}
                        </TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>{renderVehicleAmount(vehicle, 'invoicingTitleFees')}</TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                          {renderVehicleAmount(vehicle, 'invoicingRecallExpenses')}
                        </TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>{renderVehicleAmount(vehicle, 'invoicingFedEx')}</TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>{renderVehicleAmount(vehicle, 'invoicingFedExAPI')}</TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                          {renderVehicleAmount(vehicle, 'invoicingReplacementLabel')}
                        </TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>{renderVehicleAmount(vehicle, 'invoicingRepairs')}</TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                          {renderVehicleAmount(vehicle, 'invoicingVehicleCleanUp')}
                        </TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                          {renderVehicleAmount(vehicle, 'invoicingMiscellaneous')}
                        </TableCell>

                        <TableCell sx={{ width: '160px', minWidth: '160px' }}>
                          {renderVehicleAmount(vehicle, 'invoicingOutboundTransportation')}
                        </TableCell>

                        <TableCell sx={{ width: '200px', minWidth: '200px' }}>{vehicle.invoicingNotes}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>

              </Table>
            </TableContainer>

            <TablePagination
              component='div'
              count={invoicingServices.length}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
              page={page}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              SelectProps={{ fullWidth: false }}
              sx={{
                fontSize: '9pt',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
                  fontSize: '9pt'
                },
                '& .MuiTablePagination-toolbar': {
                  minHeight: 40
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Invoicing Services:**

This list displays vehicles that have been moved from the Vehicles Eligible For Invoicing list for charge adjustments.

**Edit Charges:**

Allows users to modify service charges by clicking into a cell and entering a value.

**Bulk Charge Updates:**

Enter a value in the top cell of a column to apply changes to all vehicles in the list.

Press "EACH" to apply the entered value to all vehicles in that column.

Press "SPLIT" to divide the entered value by the number of vehicles and apply the result to all vehicles.

Outbound Transportation:

An Outbound Transportation charge may be entered only when the vehicle has an outbound trip handled by Blue Water Importers or Great Lakes Car Carriers. Vehicles without an outbound trip, or transported by another company, will show a zero charge and the field will be unavailable. Unavailable fields cannot be populated manually or through "EACH" or "SPLIT". When multiple vehicles are selected, this rule is applied to each vehicle individually.

**Save Button:**

Updates the invoicing fields under Vehicle Details → Invoicing (tab) with the values in the Invoicing Services list.

**Save And Mark Selected Vehicles As Ready To Invoice:**

Moves selected vehicles to the Ready For Invoicing list and populates the "Ready To Invoice" column with the current date.`}
            </GreenText>
          </Grid>

          <Grid item container alignItems='center' justifyContent='flex-end'>
            <Grid item>
              <LoadingButton
                disabled={isSubmitting || selected.length === 0}
                loading={isSaving}
                onClick={() => {
                  setIsSaving(true)
                  saveInvoicingServices(selected, false)
                    .then(res => {
                      enqueueSnackbar('Invoicing Services Saved Successfully!', { variant: 'success' })
                      refreshInvoicing(res.value)
                      setSelected([])
                      setPage(0)
                    })
                    .finally(() => setIsSaving(false))
                }}
                size='small'
                variant='contained'
              >
                SAVE
              </LoadingButton>
            </Grid>

            <Grid item>
              <LoadingButton
                disabled={isSubmitting || selected.length === 0}
                loading={isSavingAndMarkingReadyToInvoice}
                onClick={() => {
                  setIsSavingAndMarkingReadyToInvoice(true)
                  saveInvoicingServices(selected, true)
                    .then(res => {
                      enqueueSnackbar('Invoicing Services Saved Successfully!', { variant: 'success' })
                      refreshInvoicing(res.value)
                      setSelected([])
                      setPage(0)
                    })
                    .finally(() => setIsSavingAndMarkingReadyToInvoice(false))
                }}
                size='small'
                variant='contained'
              >
                SAVE AND MARK SELECTED VEHICLES AS READY TO INVOICE
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
